import type { EntityState} from "@@/shared/entity-status";
import { entityStateZodSchema } from "@@/shared/entity-status";
import { idZodSchema } from "@@/shared/id";
import type {
    MetaContent} from "@@/shared/meta-content/meta-content";
import {
    metaContentZodSchema,
} from "@@/shared/meta-content/meta-content";
import { emptyString } from "@@/utilities/empty";
import { generateId } from "@@/utilities/generate-id";
import { z } from "zod";
import type {
    IsoAndUnixTimestamp} from "../../date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "../../date-time/iso-and-unix";

type DeliveryZoneId = string;
type DeliveryZone = {
    readonly _id: DeliveryZoneId;
    readonly _type: "DELIVERY_ZONE";
    readonly _status: EntityState;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly meta: MetaContent;
    readonly shortIdentifier: string;
    readonly zipCodes: string[];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isDeliveryZoneId = (id: any): id is DeliveryZoneId => {
    return typeof id === "string" && id.startsWith("delivery_zone_");
};

const createDeliveryZone = (
    params: Omit<DeliveryZone, "_id" | "_type" | "_status" | "_createdAt"> & {
        _id?: DeliveryZoneId;
    },
): DeliveryZone => {
    // Clean zip codes
    const zipCodes = params.zipCodes
        .map(zipCode => zipCode.replace(/[^a-z0-9]/gim, emptyString))
        .filter(Boolean);
    // Create delivery zone
    return {
        ...params,
        _id: params._id ?? generateId({ prefix: "delivery_zone__" }),
        _type: "DELIVERY_ZONE",
        _status: "ACTIVE",
        _createdAt: nowIsoAndUnix(),
        zipCodes,
    };
};

const deliveryZoneZodSchema: z.ZodSchema<DeliveryZone> = z.object({
    _id: idZodSchema,
    _type: z.literal("DELIVERY_ZONE"),
    _status: entityStateZodSchema,
    _createdAt: isoAndUnixZodSchema,
    meta: metaContentZodSchema,
    zipCodes: z.array(z.string()).min(1),
    shortIdentifier: z.string().min(1),
});

export { createDeliveryZone, deliveryZoneZodSchema, isDeliveryZoneId };
export type { DeliveryZone, DeliveryZoneId };
